import type { CSSProperties } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { portfolioProjects } from '../data/portfolio';
import type { PortfolioProject } from '../types/portfolio';

const gridSpans = [
  'lg:col-span-7',
  'lg:col-span-5',
  'lg:col-span-4',
  'lg:col-span-4',
  'lg:col-span-4',
  'lg:col-span-12',
];

const ProjectCard = ({ project, index }: { project: PortfolioProject; index: number }) => {
  const titleId = `project-${project.id}-title`;
  const summaryId = `project-${project.id}-summary`;
  const previewStyle = {
    '--project-accent': project.accent,
    '--project-surface': project.surface,
    '--project-foreground': project.foreground,
    background: project.surface,
    color: project.foreground,
  } as CSSProperties;

  return (
    <motion.article
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      whileHover={{ y: -8 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.6, delay: (index % 2) * 0.08, ease: [0.23, 1, 0.32, 1] }}
      className={`${gridSpans[index]} project-card group overflow-hidden rounded-[1.75rem] border border-line/10 bg-panel transition-colors hover:border-line/25`}
      aria-labelledby={titleId}
      aria-describedby={summaryId}
      data-agent-id={`agent://consltr/portfolio/${project.id}`}
      data-semantic-intent="read"
    >
      <div className="flex h-full flex-col">
        <div
          className="project-preview relative min-h-72 overflow-hidden p-7 sm:min-h-80 sm:p-9"
          style={previewStyle}
        >
          <div className="project-noise absolute inset-0 opacity-30" aria-hidden="true" />
          <div
            className="absolute -right-16 -top-20 h-72 w-72 rounded-full opacity-25 blur-3xl transition-transform duration-700 group-hover:scale-125"
            style={{ backgroundColor: project.accent }}
            aria-hidden="true"
          />
          <div className="relative z-10 flex h-full min-h-56 flex-col justify-between sm:min-h-64">
            <div className="flex items-start justify-between">
              <span
                className="flex h-14 min-w-14 items-center justify-center rounded-2xl border px-3 font-mono text-lg font-semibold"
                style={{ borderColor: `${project.accent}88`, color: project.accent }}
              >
                {project.mark}
              </span>
              <span className="rounded-full border border-current/20 px-3 py-1 font-mono text-[0.65rem] uppercase tracking-[0.18em] opacity-60">
                {project.status}
              </span>
            </div>
            <div>
              <p className="mb-3 font-mono text-xs uppercase tracking-[0.18em] opacity-60">
                {project.category}
              </p>
              <div className="flex items-end justify-between gap-5">
                <h3
                  id={titleId}
                  className="magazine-title max-w-xl text-4xl leading-none tracking-[-0.04em] sm:text-5xl"
                >
                  {project.name}
                </h3>
                <span
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1"
                  style={{ backgroundColor: project.accent, color: project.surface }}
                  aria-hidden="true"
                >
                  <ArrowUpRight size={20} />
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-1 flex-col p-7 sm:p-8">
          <p id={summaryId} className="text-base leading-7 text-muted">
            {project.summary}
          </p>

          <dl className="mt-6 grid gap-6 border-t border-line/10 pt-6 sm:grid-cols-2">
            <div>
              <dt className="font-mono text-[0.65rem] uppercase tracking-[0.16em] text-faint">
                Consltr contribution
              </dt>
              <dd className="mt-2 text-sm leading-6 text-ink">{project.contribution}</dd>
            </div>
            <div>
              <dt className="font-mono text-[0.65rem] uppercase tracking-[0.16em] text-faint">
                Status / outcome
              </dt>
              <dd className="mt-2 text-sm leading-6 text-ink">{project.outcome}</dd>
            </div>
            <div>
              <dt className="font-mono text-[0.65rem] uppercase tracking-[0.16em] text-faint">
                Technology stack
              </dt>
              <dd className="mt-2">
                <ul
                  className="space-y-2 text-sm leading-6 text-ink"
                  aria-label={`${project.name} technology stack`}
                >
                  {project.technology.map(item => (
                    <li key={item.layer}>
                      <strong className="font-semibold">{`${item.layer}:`}</strong> {item.tools}
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
            <div>
              <dt className="font-mono text-[0.65rem] uppercase tracking-[0.16em] text-faint">
                System design
              </dt>
              <dd className="mt-2 text-sm leading-6 text-ink">{project.systemDesign}</dd>
            </div>
          </dl>

          {project.metric && (
            <div className="mt-6 flex items-baseline gap-3 rounded-2xl bg-ink/[0.04] px-4 py-3">
              <strong className="serif text-2xl font-normal text-ink">
                {project.metric.value}
              </strong>
              <span className="text-xs leading-5 text-faint">{project.metric.label}</span>
            </div>
          )}

          <ul className="mt-6 flex flex-wrap gap-2" aria-label={`${project.name} capabilities`}>
            {project.services.map(service => (
              <li
                key={service}
                className="rounded-full border border-line/10 px-3 py-1.5 text-xs text-faint"
              >
                {service}
              </li>
            ))}
          </ul>

          <a
            href={project.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex w-fit items-center gap-2 font-mono text-xs font-semibold text-ink underline decoration-line/30 underline-offset-4 transition-colors hover:text-accent"
            aria-label={`Visit ${project.name} at ${project.displayUrl}; opens in a new tab`}
            data-agent-id={`agent://consltr/portfolio/${project.id}/website`}
            data-semantic-intent="open"
          >
            Visit {project.displayUrl}
            <ArrowUpRight size={14} aria-hidden="true" />
          </a>
        </div>
      </div>
    </motion.article>
  );
};

const Portfolio = () => (
  <section id="portfolio" className="border-t border-line/10 bg-panel-alt py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="mb-14 grid gap-8 md:grid-cols-[0.7fr_1fr] md:items-end"
      >
        <div>
          <p className="section-kicker">Selected work</p>
          <h2 className="magazine-title mt-4 text-5xl tracking-[-0.045em] sm:text-7xl">
            Built in close partnership.
          </h2>
        </div>
        <p className="max-w-2xl text-lg leading-8 text-muted md:justify-self-end">
          What each product is, what Consltr contributed, how the system works, the technology
          behind it, and where the work stands now—six examples of strategy, design, engineering,
          and applied AI moving together.
        </p>
      </motion.div>

      <div className="grid gap-5 lg:grid-cols-12">
        {portfolioProjects.map((project, index) => (
          <ProjectCard key={project.id} project={project} index={index} />
        ))}
      </div>
    </div>
  </section>
);

export default Portfolio;
